import type { ReactNode } from 'react'
import { LoaderCircle, TriangleAlert } from 'lucide-react'

/* The three states every account list can be in besides having rows: still
   loading, refused by the network, and genuinely empty.

   Written once because four lists showing "nothing here yet" four different
   ways is four different readings of the same fact — and because the empty
   case on these pages is not a failure. A buyer with no bookings has no
   bookings; the page should say so plainly and point at the floor. */

export function Loading({ label }: { label: string }) {
  return (
    <div className="grid min-h-[240px] place-items-center rounded-2xl border border-line bg-raised/40">
      <LoaderCircle
        aria-label={label}
        className="size-5 animate-spin text-dim"
        strokeWidth={2}
      />
    </div>
  )
}

/** The API refused or could not be reached. Retrying is the whole of the
    handling: nothing was written, and the button is the fix. */
export function Problem({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-line bg-raised/60 px-6 py-12 text-center">
      <span
        aria-hidden="true"
        className="mx-auto grid size-12 place-items-center rounded-2xl bg-surface text-lime"
      >
        <TriangleAlert className="size-5" strokeWidth={1.8} />
      </span>
      <p className="mt-4 text-[15px] font-semibold text-platinum">We could not load that</p>
      <p className="mx-auto mt-2 max-w-[42ch] text-[13.5px] leading-relaxed text-steel">
        The showroom's system did not answer. Your records are safe — this page just could not
        reach them.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 rounded-full border border-lime/55 px-5 py-2.5 text-[13px] font-semibold text-platinum transition hover:border-lime hover:bg-lime/10"
      >
        Try again
      </button>
    </div>
  )
}

export function EmptyAccount({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <div className="rounded-2xl border border-line bg-raised/60 px-6 py-14 text-center">
      <span
        aria-hidden="true"
        className="mx-auto grid size-12 place-items-center rounded-2xl bg-surface text-lime"
      >
        {icon}
      </span>
      <p className="mt-4 text-[15px] font-semibold text-platinum">{title}</p>
      <p className="mx-auto mt-2 max-w-[46ch] text-[13.5px] leading-relaxed text-steel">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}
